import 'photoswipe/style.css';

import { photoswipe } from '@/storybook/theme';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import { onUnmounted } from 'vue';

const CONTROL_SIZE_PX = 50;
const CONTROL_GAP_PX = 8;
const CONTROL_TOP_PX = 10;
const CONTROL_RIGHT_MARGIN_PX = 12;

const visibleViewportWidth = () =>
  Math.min(window.innerWidth, window.outerWidth || window.innerWidth, document.documentElement.clientWidth || window.innerWidth);

const setImportantStyle = (element: HTMLElement, property: string, value: string) => {
  if (element.style.getPropertyValue(property) === value && element.style.getPropertyPriority(property) === 'important') {
    return;
  }

  element.style.setProperty(property, value, 'important');
};

const positionLightboxControls = () => {
  const viewportWidth = visibleViewportWidth();
  const closeButton = document.querySelector<HTMLElement>('.pswp__button--close');
  const zoomButton = document.querySelector<HTMLElement>('.pswp__button--zoom');

  const applyPosition = (button: HTMLElement | null, rightOffset: number) => {
    if (!button) {
      return;
    }

    const left = Math.max(CONTROL_RIGHT_MARGIN_PX, viewportWidth - rightOffset - CONTROL_SIZE_PX);
    setImportantStyle(button, 'left', `${left}px`);
    setImportantStyle(button, 'right', 'auto');
    setImportantStyle(button, 'top', `${CONTROL_TOP_PX}px`);
  };

  applyPosition(closeButton, CONTROL_RIGHT_MARGIN_PX);
  applyPosition(zoomButton, CONTROL_RIGHT_MARGIN_PX + CONTROL_SIZE_PX + CONTROL_GAP_PX);
};

export const usePhotoSwipe = (src: string) => {
  let lightbox: PhotoSwipeLightbox | null = null;
  let controlObserver: MutationObserver | null = null;
  let controlRafId: number | undefined;
  let controlTimeoutIds: number[] = [];

  const requestLightboxControlPositioning = () => {
    if (controlRafId !== undefined) {
      return;
    }

    controlRafId = requestAnimationFrame(() => {
      controlRafId = undefined;
      positionLightboxControls();
    });
  };

  const stopLightboxControlPositioning = () => {
    controlObserver?.disconnect();
    controlObserver = null;

    if (controlRafId !== undefined) {
      cancelAnimationFrame(controlRafId);
      controlRafId = undefined;
    }

    controlTimeoutIds.forEach(timeoutId => window.clearTimeout(timeoutId));
    controlTimeoutIds = [];
  };

  const scheduleLightboxControlPositioning = () => {
    positionLightboxControls();
    requestLightboxControlPositioning();

    controlTimeoutIds.forEach(timeoutId => window.clearTimeout(timeoutId));
    controlTimeoutIds = [50, 150, 300, 600, 1000, 2000].map(delay => window.setTimeout(positionLightboxControls, delay));

    if (!controlObserver && document.body) {
      controlObserver = new MutationObserver(requestLightboxControlPositioning);
      controlObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class', 'style'],
        childList: true,
        subtree: true,
      });
    }
  };

  const init = (width?: number, height?: number) => {
    lightbox = new PhotoSwipeLightbox({
      dataSource: [
        {
          width,
          height,
          src,
        },
      ],
      arrowKeys: false,
      initialZoomLevel: 'fit',
      zoomSVG: photoswipe.zoomSVGString,
      closeSVG: photoswipe.closeSVGString,
      counter: false,
      preloadFirstSlide: true,
      bgOpacity: 0.8,
      pswpModule: () => import('photoswipe'),
    });

    lightbox.on('afterInit', scheduleLightboxControlPositioning);
    lightbox.on('bindEvents', scheduleLightboxControlPositioning);
    lightbox.on('openingAnimationStart', scheduleLightboxControlPositioning);
    lightbox.on('openingAnimationEnd', scheduleLightboxControlPositioning);
    lightbox.on('resize', scheduleLightboxControlPositioning);
    lightbox.on('close', stopLightboxControlPositioning);
    lightbox.on('destroy', stopLightboxControlPositioning);
    lightbox?.init();
  };

  const open = () => {
    lightbox?.loadAndOpen(0);
    scheduleLightboxControlPositioning();
  };

  onUnmounted(() => {
    if (lightbox) {
      lightbox.destroy();
      lightbox = null;
    }
  });

  return { lightbox, open, init };
};
